import React from 'react'
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Pagination,
  Button,
  Stack,
} from '@mui/material'
import { Add as AddIcon, SearchOff as SearchOffIcon } from '@mui/icons-material'
import { Cigar } from '@store/api/cigarApi'
import CigarCard from '@components/cigars/CigarCard'
import { TOUCH_TARGET_MIN_PX } from '@components/layout/layoutConstants'

interface CollectionListProps {
  cigars?: Cigar[]
  isLoading: boolean
  error?: unknown
  page: number
  totalPages: number
  totalCount: number
  hasActiveFilters: boolean
  onPageChange: (page: number) => void
  onEdit?: (cigar: Cigar) => void
  onDelete?: (cigarId: string) => void
  onMove?: (cigar: Cigar) => void
  onView?: (cigar: Cigar) => void
  onStartSession?: (cigar: Cigar) => void
  onAddCigar: () => void
  onClearFilters: () => void
  onEmptyStateCta?: () => void
  selectionMode?: boolean
  selectedCigarIds?: Set<string>
  onToggleCigarSelection?: (cigarId: string) => void
}

export const CollectionList: React.FC<CollectionListProps> = ({
  cigars,
  isLoading,
  error,
  page,
  totalPages,
  totalCount,
  hasActiveFilters,
  onPageChange,
  onEdit,
  onDelete,
  onMove,
  onView,
  onStartSession,
  onAddCigar,
  onClearFilters,
  onEmptyStateCta,
  selectionMode = false,
  selectedCigarIds,
  onToggleCigarSelection,
}) => {
  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Alert severity="error">
        Failed to load cigars. Please try again.
      </Alert>
    )
  }

  if (!cigars || cigars.length === 0) {
    return (
      <Card>
        <CardContent>
          <Box textAlign="center" py={6}>
            <SearchOffIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {hasActiveFilters ? 'No cigars match your filters' : 'Your collection is empty'}
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              {hasActiveFilters
                ? 'Try adjusting or clearing your search criteria to see more results.'
                : 'Start building your cigar collection by adding your first cigar.'}
            </Typography>
            <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap" useFlexGap>
              {hasActiveFilters && (
                <Button
                  variant="outlined"
                  onClick={onClearFilters}
                  sx={{ minHeight: TOUCH_TARGET_MIN_PX }}
                >
                  Clear Filters
                </Button>
              )}
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => {
                  onEmptyStateCta?.()
                  onAddCigar()
                }}
                sx={{ minHeight: TOUCH_TARGET_MIN_PX }}
              >
                Add cigar
              </Button>
            </Stack>
          </Box>
        </CardContent>
      </Card>
    )
  }

  const itemsPerPage = totalPages > 0 ? Math.ceil(totalCount / totalPages) : totalCount

  return (
    <>
      <Box>
        {cigars.map((cigar) => (
          <CigarCard
            key={cigar.id}
            cigar={cigar}
            onEdit={onEdit}
            onDelete={onDelete}
            onMove={onMove ? (c) => onMove(c) : undefined}
            onView={onView}
            onStartSession={onStartSession}
            viewMode="list"
            selectionMode={selectionMode}
            selected={selectedCigarIds?.has(cigar.id)}
            onToggleSelect={onToggleCigarSelection}
          />
        ))}
      </Box>

      {totalPages > 1 && (
        <Box display="flex" justifyContent="center" alignItems="center" mt={4} gap={2}>
          <Typography variant="body2" color="text.secondary">
            Showing {((page - 1) * itemsPerPage) + 1}–{Math.min(page * itemsPerPage, totalCount)} of {totalCount}
          </Typography>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, value) => onPageChange(value)}
            color="primary"
            showFirstButton
            showLastButton
          />
        </Box>
      )}
    </>
  )
}
